/**
 * Inlined literals for the three `CitizenshipStatus` members this reducer maps.
 *
 * Why this file exists: `vg-interaction-model` ships as a single ~8.9 MB bundled module, so
 * importing one enum member as a *value* loads the whole library. This reducer is reached through
 * the reducers barrel by many suites that never touch the interaction model otherwise, making that
 * bundle one of the most expensive modules in the test run.
 *
 * `import type` is erased at emit, so this module requires nothing at runtime. The 79 other files
 * that genuinely use the library's services, actions and selectors still import it normally.
 *
 * The literals cannot silently drift. Each raw literal is declared *without* a cast and then
 * asserted against the real enum member, so changing one here — or the library changing its value —
 * fails `tsc`. Asserting before the cast matters: casting first would satisfy the assertion
 * trivially and the guard would be worthless.
 */
import type { CitizenshipStatus } from 'vg-interaction-model/dist';

// Raw, uncast literals — these are what the assertions below actually check.
const US = 'US';
const RA = 'RA';
const NR = 'NR';

type Assert<T extends true> = T;

/** Compile-time proof that the inlined literals still match the library enum. */
export type CitizenshipStatusLiteralsInSync = [
  Assert<`${CitizenshipStatus.UsCitizen}` extends typeof US ? true : false>,
  Assert<`${CitizenshipStatus.ResidentAlien}` extends typeof RA ? true : false>,
  Assert<`${CitizenshipStatus.NonresidentAlien}` extends typeof NR ? true : false>,
];

// Cast to the specific enum members, so switch-case narrowing behaves exactly as before.
export const CITIZENSHIP_US_CITIZEN = US as CitizenshipStatus.UsCitizen;
export const CITIZENSHIP_RESIDENT_ALIEN = RA as CitizenshipStatus.ResidentAlien;
export const CITIZENSHIP_NONRESIDENT_ALIEN = NR as CitizenshipStatus.NonresidentAlien;
